import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { 
  Search, User, Mail, Calendar, Crown, Building2, 
  MoreVertical, Ban, Trash2, Shield, ChevronLeft, ChevronRight,
  Stethoscope, UserPlus, Heart
} from "lucide-react";
import { toast } from "sonner";

const AdminUsers = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showActions, setShowActions] = useState(null);
  
  // Partner assignment
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [userToAssign, setUserToAssign] = useState(null);
  
  // Dietitian assignment
  const [showDietitianDialog, setShowDietitianDialog] = useState(false);
  const [dietitians, setDietitians] = useState([]);
  const [selectedDietitian, setSelectedDietitian] = useState("");

  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [page, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const status = statusFilter === "all" ? "" : `&status=${statusFilter}`;
      const response = await fetch(
        `${API}/admin/users?skip=${page * limit}&limit=${limit}${status}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotal(data.total);
      }
    } catch (error) {
      console.error("Fetch users error:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await fetch(`${API}/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners || []);
      }
    } catch (error) {
      console.error("Fetch partners error:", error);
    }
  };

  const fetchDietitians = async () => {
    try {
      const response = await fetch(`${API}/admin/dietitians`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        setDietitians(data.dietitians || []);
      }
    } catch (error) {
      console.error("Fetch dietitians error:", error);
    }
  };

  const updateUser = async (userId, updateData) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify(updateData),
      });
      
      if (response.ok) {
        toast.success("User updated");
        fetchUsers();
        setShowActions(null);
      } else {
        toast.error("Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user? This cannot be undone.")) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        toast.success("User deleted");
        fetchUsers();
        setShowActions(null);
      } else {
        toast.error("Failed to delete user");
      }
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const assignPartner = async () => {
    if (!userToAssign || !selectedPartner) return;
    
    try {
      const response = await fetch(`${API}/admin/users/${userToAssign.user_id}/assign-partner`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({ user_id: userToAssign.user_id, partner_id: selectedPartner }),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchUsers();
        setShowPartnerDialog(false);
        setUserToAssign(null);
        setSelectedPartner("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to assign partner");
      }
    } catch (error) {
      toast.error("Failed to assign partner");
    }
  };

  const assignDietitian = async () => {
    if (!userToAssign || !selectedDietitian) return;
    
    try {
      const response = await fetch(`${API}/admin/patients/${userToAssign.user_id}/assign-dietitian`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({ patient_id: userToAssign.user_id, dietitian_id: selectedDietitian }),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchUsers();
        setShowDietitianDialog(false);
        setUserToAssign(null);
        setSelectedDietitian("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to assign dietitian");
      }
    } catch (error) {
      toast.error("Failed to assign dietitian");
    }
  };

  const setUserRole = async (userId, role) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/set-role?role=${role}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        toast.success(`User role set to ${role}`);
        fetchUsers();
        setShowActions(null);
      } else {
        toast.error("Failed to set role");
      }
    } catch (error) {
      toast.error("Failed to set role");
    }
  };

  const openPartnerDialog = (user) => {
    setUserToAssign(user);
    fetchPartners();
    setShowPartnerDialog(true);
    setShowActions(null);
  };

  const openDietitianDialog = (user) => {
    setUserToAssign(user);
    fetchDietitians();
    setShowDietitianDialog(true);
    setShowActions(null);
  };

  const getStatusBadge = (user) => {
    const status = user.subscription_status || "free";
    const badges = {
      "free": "bg-gray-100 text-gray-700",
      "trial": "bg-warning/10 text-warning",
      "active": "bg-primary/10 text-primary",
      "monthly_active": "bg-primary/10 text-primary",
      "yearly_active": "bg-primary/10 text-primary",
      "partner": "bg-success/10 text-success",
    };
    
    const labels = {
      "free": "Free",
      "trial": "Trial",
      "active": "Premium",
      "monthly_active": "Monthly",
      "yearly_active": "Yearly",
      "partner": "Partner",
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || badges.free}`}>
        {labels[status] || "Free"}
      </span>
    );
  };

  const getRoleBadge = (user) => {
    // Distinguish wellness users from clinical patients
    const displayRole = (user.role === "patient" && user.program_type === "wellness") ? "wellness" : user.role;
    const badges = {
      "admin": { bg: "bg-purple-100 text-purple-700", icon: Shield, label: "Admin" },
      "dietitian": { bg: "bg-blue-100 text-blue-700", icon: Stethoscope, label: "Physician" },
      "partner_admin": { bg: "bg-green-100 text-green-700", icon: Building2, label: "Partner Admin" },
      "corporate_admin": { bg: "bg-emerald-100 text-emerald-700", icon: Building2, label: "Corporate Admin" },
      "patient": { bg: "bg-orange-100 text-orange-700", icon: User, label: "Patient" },
      "wellness": { bg: "bg-teal-100 text-teal-700", icon: Heart, label: "Wellness" },
      "user": { bg: "bg-gray-100 text-gray-600", icon: User, label: "User" },
    };
    
    const badge = badges[displayRole] || badges.user;
    const Icon = badge.icon;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} flex items-center gap-1 w-fit`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const filteredUsers = users.filter(user => 
    !searchQuery || 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-xl">
            <SelectValue placeholder="All users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="partner">Partner</SelectItem>
            <SelectItem value="wellness">Wellness</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users List */}
      <Card className="border border-orange-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-orange-50/50">
                <tr>
                  <th className="text-left p-4 font-medium text-secondary">User</th>
                  <th className="text-left p-4 font-medium text-secondary">Role</th>
                  <th className="text-left p-4 font-medium text-secondary">Status</th>
                  <th className="text-left p-4 font-medium text-secondary hidden md:table-cell">Signup Date</th>
                  <th className="text-left p-4 font-medium text-secondary hidden lg:table-cell">Meals</th>
                  <th className="text-right p-4 font-medium text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.user_id} className="border-t border-orange-100 hover:bg-orange-50/30">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-secondary">
                            {user.name || "No name"}
                          </p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Select
                        value={user.program_type === "wellness" ? "wellness" : user.role}
                        onValueChange={(newRole) => {
                          if (newRole === "wellness") {
                            setUserRole(user.user_id, "patient");
                          } else {
                            setUserRole(user.user_id, newRole);
                          }
                        }}
                      >
                        <SelectTrigger className="w-[140px] h-8 text-xs rounded-lg border-orange-100" data-testid={`role-select-${user.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="dietitian">Physician</SelectItem>
                          <SelectItem value="partner_admin">Partner Admin</SelectItem>
                          <SelectItem value="corporate_admin">Corporate Admin</SelectItem>
                          <SelectItem value="patient">Patient</SelectItem>
                          <SelectItem value="wellness">Wellness</SelectItem>
                          <SelectItem value="user">User</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1">
                        <Select
                          value={user.subscription_status || "free"}
                          onValueChange={(status) => updateUser(user.user_id, { subscription_status: status })}
                        >
                          <SelectTrigger className="w-[120px] h-8 text-xs rounded-lg border-orange-100" data-testid={`status-select-${user.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="partner">Partner</SelectItem>
                          </SelectContent>
                        </Select>
                        {user.is_suspended && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
                            Suspended
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <p className="text-sm text-muted-foreground">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
                      </p>
                    </td>
                    <td className="p-4 hidden lg:table-cell">
                      <p className="text-sm text-muted-foreground">
                        {user.meal_analysis_count || 0}
                      </p>
                    </td>
                    <td className="p-4 text-right">
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowActions(showActions === user.user_id ? null : user.user_id)}
                          className="rounded-full"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                        
                        {showActions === user.user_id && (
                          <div className="absolute right-0 top-full mt-1 bg-white border border-orange-100 rounded-xl shadow-lg z-10 min-w-[180px]">
                            {/* Role Management */}
                            <div className="px-3 py-2 border-b border-orange-100">
                              <p className="text-xs text-muted-foreground font-medium">Set Role</p>
                            </div>
                            {user.role !== "admin" && (
                              <button
                                onClick={() => setUserRole(user.user_id, "admin")}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-orange-50 text-left"
                              >
                                <Shield className="w-4 h-4 text-purple-600" />
                                Make Admin
                              </button>
                            )}
                            {user.role !== "dietitian" && (
                              <button
                                onClick={() => setUserRole(user.user_id, "dietitian")}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-orange-50 text-left"
                              >
                                <Stethoscope className="w-4 h-4 text-blue-600" />
                                Make Dietitian
                              </button>
                            )}
                            {user.role !== "partner_admin" && (
                              <button
                                onClick={() => setUserRole(user.user_id, "partner_admin")}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-orange-50 text-left"
                              >
                                <Building2 className="w-4 h-4 text-green-600" />
                                Make Partner Admin
                              </button>
                            )}
                            {user.role !== "patient" && user.role !== "user" && (
                              <button
                                onClick={() => setUserRole(user.user_id, "patient")}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-orange-50 text-left"
                              >
                                <User className="w-4 h-4 text-orange-600" />
                                Make Patient
                              </button>
                            )}
                            
                            {/* Assignment Actions */}
                            <div className="px-3 py-2 border-t border-b border-orange-100">
                              <p className="text-xs text-muted-foreground font-medium">Assignments</p>
                            </div>
                            <button
                              onClick={() => openPartnerDialog(user)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-orange-50 text-left"
                            >
                              <Building2 className="w-4 h-4" />
                              Assign to Partner
                            </button>
                            <button
                              onClick={() => openDietitianDialog(user)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-orange-50 text-left"
                            >
                              <Stethoscope className="w-4 h-4" />
                              Assign Dietitian
                            </button>
                            
                            {/* Account Actions */}
                            <div className="border-t border-orange-100">
                              {!user.is_suspended ? (
                                <button
                                  onClick={() => updateUser(user.user_id, { is_suspended: true })}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-orange-50 text-left text-warning"
                                >
                                  <Ban className="w-4 h-4" />
                                  Suspend
                                </button>
                              ) : (
                                <button
                                  onClick={() => updateUser(user.user_id, { is_suspended: false })}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-orange-50 text-left text-success"
                                >
                                  <User className="w-4 h-4" />
                                  Unsuspend
                                </button>
                              )}
                              <button
                                onClick={() => deleteUser(user.user_id)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-destructive/10 text-left text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-full"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-full"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Assign Partner Dialog */}
      <Dialog open={showPartnerDialog} onOpenChange={setShowPartnerDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Assign User to Partner Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {userToAssign && (
              <div className="bg-orange-50 rounded-xl p-3">
                <p className="font-medium text-secondary">{userToAssign.name}</p>
                <p className="text-sm text-muted-foreground">{userToAssign.email}</p>
              </div>
            )}
            <Select value={selectedPartner} onValueChange={setSelectedPartner}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Select a partner organization" />
              </SelectTrigger>
              <SelectContent>
                {partners.map((partner) => (
                  <SelectItem key={partner.partner_id} value={partner.partner_id}>
                    {partner.organization_name} ({partner.partner_type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowPartnerDialog(false)} className="rounded-full">
                Cancel
              </Button>
              <Button onClick={assignPartner} disabled={!selectedPartner} className="rounded-full bg-primary">
                Assign Partner
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dietitian Dialog */}
      <Dialog open={showDietitianDialog} onOpenChange={setShowDietitianDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Assign Dietitian to Patient</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {userToAssign && (
              <div className="bg-orange-50 rounded-xl p-3">
                <p className="font-medium text-secondary">{userToAssign.name}</p>
                <p className="text-sm text-muted-foreground">{userToAssign.email}</p>
              </div>
            )}
            {dietitians.length > 0 ? (
              <Select value={selectedDietitian} onValueChange={setSelectedDietitian}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select a dietitian" />
                </SelectTrigger>
                <SelectContent>
                  {dietitians.map((dietitian) => (
                    <SelectItem key={dietitian.user_id} value={dietitian.user_id}>
                      {dietitian.name} ({dietitian.patient_count || 0} patients)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No dietitians found. Create a dietitian account first.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowDietitianDialog(false)} className="rounded-full">
                Cancel
              </Button>
              <Button 
                onClick={assignDietitian} 
                disabled={!selectedDietitian || dietitians.length === 0} 
                className="rounded-full bg-primary"
              >
                Assign Dietitian
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
